import React from 'react';
import { Link } from 'react-router-dom';
import { LockOutlined } from '@ant-design/icons';
import styled, { css } from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import usePerx3Redesign from 'hooks/usePerx3Redesign';

import { TOrbit } from '../i18n/TModules';
import { orbitTokens, orbitType } from '../styles';

export interface IOrbitAccessCardProps {
  /** The server's restriction text. Falls back to the frame's generic copy when absent. */
  body?: string;
  /**
   * TODO(AF-14): `4952:6269` draws a `View Policy Details` link, but no route in the dashboard
   * documents Orbit's data policy. The link renders only once a destination is settled.
   */
  policyPath?: string;
}

/**
 * div.access-card `4952:6262`. §8's stroke note reads `strokeWeight` 0 with a per-side border of 3
 * on the left, so this is a left rule in the danger colour, not an all-round stroke.
 */
const Card = styled.div<{ $perx3: boolean }>`
  padding: 22px 20px;
  background: ${orbitTokens.surfaceDanger};
  border-left: 3px solid ${orbitTokens.danger};
  border-radius: 10px;
  text-align: center;

  /* PERX3-REDESIGN round 8: a notice, not an error. The left rule goes - border after
     border-left resets all four sides - and the card takes the neutral ground and hairline every
     other card on the light panel carries. */
  ${({ $perx3 }) =>
    $perx3 &&
    css`
      background: ${orbitTokens.accessCardBg};
      border: 1px solid ${orbitTokens.accessCardBorder};
    `}

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: 8px;
  }
`;

const IconTile = styled.div<{ $perx3: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: 0 auto;
  padding: 10.5px 0 11.5px;
  background: ${orbitTokens.danger};
  border-radius: 22px;
  font-size: 20px;
  line-height: 20px;

  /* The circle softens with the card, and the lock is inked rather than left to inherit the body
     colour it drew before. */
  ${({ $perx3 }) =>
    $perx3 &&
    css`
      background: ${orbitTokens.accessIconBg};
      color: ${orbitTokens.accessIconInk};
    `}
`;

const Title = styled.h4`
  margin: 0;
  padding-top: 6px;
  color: ${orbitTokens.text};
  ${orbitType.containerTitle}
  font-weight: 700;
`;

const Body = styled.p`
  margin: 0;
  color: ${orbitTokens.textSecondary};
  ${orbitType.containerBody}
  font-weight: 400;
`;

// `4952:6269` sits outside the card, at the card's own width and left-aligned against its centred
// contents.
const PolicyLink = styled(Link)`
  display: inline-block;
  margin-top: 11px;
  color: ${orbitTokens.action};
  ${orbitType.containerBody}
  font-weight: 700;

  &:focus-visible {
    outline: 2px solid ${orbitTokens.action};
    outline-offset: 1px;
  }
`;

const OrbitAccessCard: React.FC<IOrbitAccessCardProps> = ({ body, policyPath }) => {
  const { t } = useMultiLanguageTranslation();
  const perx3 = usePerx3Redesign();

  return (
    <div data-testid="orbit-access">
      <Card $perx3={perx3} data-testid="orbit-access-card">
        <IconTile $perx3={perx3} aria-hidden="true">
          {perx3 ? <LockOutlined /> : '🔒'}
        </IconTile>
        <Title>{t(`${TOrbit}.access.title`)}</Title>
        <Body data-testid="orbit-access-body">
          {body !== undefined && body !== '' ? body : t(`${TOrbit}.access.body`)}
        </Body>
      </Card>
      {policyPath !== undefined ? (
        <PolicyLink to={policyPath} data-testid="orbit-access-policy">
          {t(`${TOrbit}.access.policyLink`)}
        </PolicyLink>
      ) : null}
    </div>
  );
};

export default OrbitAccessCard;
